import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { readableAccentText } from './accentText';
import { useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

// Fixed sparkle positions spread across the viewport for the ambient shimmer
// (Blush + Indigo). Percent-based so it adapts to any screen size.
const AMBIENCE_SPARKS = [
  { top: '9%', left: '15%', size: 14, delay: '0s' },
  { top: '19%', left: '80%', size: 10, delay: '1.1s' },
  { top: '30%', left: '7%', size: 9, delay: '2.3s' },
  { top: '41%', left: '88%', size: 12, delay: '0.6s' },
  { top: '53%', left: '20%', size: 9, delay: '3s' },
  { top: '62%', left: '73%', size: 11, delay: '1.7s' },
  { top: '71%', left: '11%', size: 9, delay: '2.6s' },
  { top: '83%', left: '84%', size: 13, delay: '0.9s' },
  { top: '90%', left: '38%', size: 9, delay: '2s' },
  { top: '24%', left: '47%', size: 8, delay: '3.4s' },
  { top: '48%', left: '58%', size: 9, delay: '1.4s' },
  { top: '77%', left: '55%', size: 10, delay: '2.8s' },
];

const ThemeContext = createContext();

// Unauthenticated routes always render in Classic Blue (light) so a user
// who signed out from a dark / pink theme never lands on a login/signup
// screen with unreadable greyed-out text. Matches the mobile AuthTheme lock.
const AUTH_ROUTE_RE = /^\/(login|signup|auth\/magic|auth\/link-required|verify-email|reset|forgot)/;

// Eight shared themes — mirrored 1:1 with the mobile app (mobile/src/theme).
// Order matters: the Settings picker renders these in a 2-col × 3-row grid.
export const themes = {
  'blue': {
    name: 'Classic Blue',
    description: 'White background with blue accents',
    colors: {
      '--background': '210 40% 98%',
      '--foreground': '222 47% 11%',
      '--card': '0 0% 100%',
      '--card-foreground': '222 47% 11%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '222 47% 11%',
      '--primary': '220 85% 40%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '210 40% 96%',
      '--secondary-foreground': '222 47% 11%',
      '--muted': '210 40% 96%',
      '--muted-foreground': '215 16% 47%',
      '--accent': '48 100% 50%',
      '--accent-foreground': '222 47% 11%',
      '--border': '214 32% 91%',
      '--input': '214 32% 91%',
      '--ring': '220 85% 40%',
      '--nav-blue': '#0F52BA',
      '--nav-blue-light': '#3B82F6',
      '--signal-yellow': '#FFD700',
      '--slate-bg': '#F1F5F9',
    },
    // Same dark band as Home and the iOS Classic Blue hero. The older
    // #0F52BA → #3B82F6 gradient was the previous shade.
    headerGradient: 'linear-gradient(135deg, #0B1E3F 0%, #12294F 100%)',
    navBlue: '#0F52BA',
    navBlueLight: '#3B82F6',
    isDark: false,
  },
  'dark': {
    name: 'Classic Dark',
    description: 'Cool near-black with a real blue header',
    colors: {
      '--background': '220 40% 7%',
      '--foreground': '210 40% 96%',
      '--card': '219 32% 12%',
      '--card-foreground': '210 40% 96%',
      '--popover': '219 32% 12%',
      '--popover-foreground': '210 40% 96%',
      '--primary': '220 88% 62%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '219 28% 17%',
      '--secondary-foreground': '210 40% 96%',
      '--muted': '219 28% 17%',
      '--muted-foreground': '215 20% 74%',
      '--accent': '48 100% 50%',
      '--accent-foreground': '220 40% 8%',
      '--border': '219 24% 21%',
      '--input': '219 24% 19%',
      '--ring': '220 88% 62%',
      '--nav-blue': '#4A84F4',
      '--nav-blue-light': '#7BB3FF',
      '--signal-yellow': '#FFD700',
      '--slate-bg': '#0B0F17',
    },
    headerGradient: 'linear-gradient(135deg, #1B3A6B 0%, #0E244A 100%)',
    navBlue: '#4A84F4',
    navBlueLight: '#7BB3FF',
    isDark: true,
  },
  'sunset-light': {
    name: 'Sunset Pop Light',
    description: 'Bright magenta & violet on white',
    colors: {
      '--background': '322 70% 99%',
      '--foreground': '288 35% 16%',
      '--card': '0 0% 100%',
      '--card-foreground': '288 35% 16%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '288 35% 16%',
      '--primary': '335 92% 55%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '322 55% 96%',
      '--secondary-foreground': '288 35% 16%',
      '--muted': '320 45% 95%',
      '--muted-foreground': '300 12% 45%',
      '--accent': '186 78% 55%',
      '--accent-foreground': '288 40% 12%',
      '--border': '322 40% 90%',
      '--input': '322 40% 90%',
      '--ring': '335 92% 55%',
      '--nav-blue': '#FF3D8B',
      '--nav-blue-light': '#C238E0',
      '--signal-yellow': '#FFD700',
      '--slate-bg': '#FDF0F7',
    },
    headerGradient: 'linear-gradient(135deg, #FF4D8D 0%, #C238E0 55%, #7A3BF5 100%)',
    navBlue: '#FF3D8B',
    navBlueLight: '#C238E0',
    isDark: false,
  },
  'sunset-dark': {
    name: 'Sunset Pop Dark',
    description: 'Deep plum with neon magenta',
    colors: {
      '--background': '279 48% 8%',
      '--foreground': '322 60% 96%',
      '--card': '280 42% 13%',
      '--card-foreground': '322 60% 96%',
      '--popover': '280 42% 13%',
      '--popover-foreground': '322 60% 96%',
      '--primary': '335 100% 62%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '280 38% 18%',
      '--secondary-foreground': '322 60% 96%',
      '--muted': '280 38% 18%',
      '--muted-foreground': '315 22% 74%',
      '--accent': '186 80% 62%',
      '--accent-foreground': '279 48% 8%',
      '--border': '280 34% 24%',
      '--input': '280 34% 22%',
      '--ring': '335 100% 62%',
      '--nav-blue': '#FF3D8B',
      '--nav-blue-light': '#C238E0',
      '--signal-yellow': '#FFD700',
      '--slate-bg': '#160A1E',
    },
    headerGradient: 'linear-gradient(135deg, #FF4D8D 0%, #C238E0 55%, #7A3BF5 100%)',
    navBlue: '#FF3D8B',
    navBlueLight: '#C238E0',
    isDark: true,
  },
  'warm': {
    name: 'Warm Reading',
    description: 'Low-glare sepia, easy on the eyes',
    colors: {
      '--background': '44 45% 90%',
      '--foreground': '30 28% 17%',
      '--card': '43 55% 95%',
      '--card-foreground': '30 28% 17%',
      '--popover': '43 55% 95%',
      '--popover-foreground': '30 28% 17%',
      '--primary': '25 51% 40%',
      '--primary-foreground': '43 55% 96%',
      '--secondary': '42 38% 87%',
      '--secondary-foreground': '30 28% 17%',
      '--muted': '42 32% 85%',
      '--muted-foreground': '30 16% 40%',
      '--accent': '34 62% 48%',
      '--accent-foreground': '30 28% 15%',
      '--border': '40 28% 81%',
      '--input': '40 28% 81%',
      '--ring': '25 51% 40%',
      '--nav-blue': '#9A5B34',
      '--nav-blue-light': '#C8892B',
      '--signal-yellow': '#C8892B',
      '--slate-bg': '#F4ECD8',
    },
    headerGradient: 'linear-gradient(135deg, #5A4632 0%, #3B2F22 100%)',
    navBlue: '#9A5B34',
    navBlueLight: '#C8892B',
    isDark: false,
  },
  'indigo': {
    name: 'Midnight Indigo',
    description: 'Premium deep-indigo dark mode',
    colors: {
      '--background': '234 52% 12%',
      '--foreground': '231 100% 96%',
      '--card': '232 46% 17%',
      '--card-foreground': '231 100% 96%',
      '--popover': '232 46% 17%',
      '--popover-foreground': '231 100% 96%',
      '--primary': '231 100% 74%',
      '--primary-foreground': '234 52% 10%',
      '--secondary': '232 40% 23%',
      '--secondary-foreground': '231 100% 96%',
      '--muted': '232 40% 23%',
      '--muted-foreground': '232 35% 78%',
      '--accent': '48 100% 50%',
      '--accent-foreground': '234 52% 10%',
      '--border': '232 34% 27%',
      '--input': '232 34% 25%',
      '--ring': '231 100% 74%',
      '--nav-blue': '#7C8CFF',
      '--nav-blue-light': '#A9B0E0',
      '--signal-yellow': '#FFD700',
      '--slate-bg': '#0E1230',
    },
    headerGradient: 'linear-gradient(135deg, #3B3F8F 0%, #1E2160 100%)',
    navBlue: '#7C8CFF',
    navBlueLight: '#A9B0E0',
    isDark: true,
  },
  'blush': {
    name: 'Blush Pink',
    description: 'Soft blush & rose-gold — airy Apple "Neo" light',
    colors: {
      '--background': '335 55% 97%',
      '--foreground': '327 24% 18%',
      '--card': '0 0% 100%',
      '--card-foreground': '327 24% 18%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '327 24% 18%',
      '--primary': '344 60% 60%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '333 50% 95%',
      '--secondary-foreground': '327 24% 18%',
      '--muted': '330 45% 94%',
      '--muted-foreground': '325 14% 44%',
      '--accent': '15 58% 78%',
      '--accent-foreground': '327 30% 20%',
      '--border': '332 45% 90%',
      '--input': '332 45% 90%',
      '--ring': '344 60% 60%',
      '--nav-blue': '#D6607F',
      '--nav-blue-light': '#E9749A',
      '--signal-yellow': '#E8C9A0',
      '--slate-bg': '#FBEFF3',
    },
    headerGradient: 'linear-gradient(135deg, #F7C6D6 0%, #E9B4D2 55%, #F4D2C2 100%)',
    headerIsLight: true,
    navBlue: '#D6607F',
    navBlueLight: '#E9749A',
    isDark: false,
  },
  'wicked': {
    name: 'Wicked Purple',
    description: 'Emerald Nebula — violet & emerald in slow rotation',
    colors: {
      '--background': '257 64% 11%',
      '--foreground': '265 60% 96%',
      '--card': '261 61% 17%',
      '--card-foreground': '265 60% 96%',
      '--popover': '261 61% 17%',
      '--popover-foreground': '265 60% 96%',
      '--primary': '274 68% 59%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '260 55% 23%',
      '--secondary-foreground': '265 60% 96%',
      '--muted': '260 42% 24%',
      '--muted-foreground': '265 28% 76%',
      '--accent': '147 60% 51%',
      '--accent-foreground': '147 60% 10%',
      '--border': '262 48% 24%',
      '--input': '262 48% 24%',
      '--ring': '274 68% 59%',
      '--nav-blue': '#9D4EDD',
      '--nav-blue-light': '#C08CF0',
      '--signal-yellow': '#35D07A',
      '--slate-bg': '#140A2E',
    },
    headerGradient: 'linear-gradient(125deg, #7B2FD6 0%, #5B2E8C 42%, #2E7D4F 72%, #35D07A 100%)',
    navBlue: '#9D4EDD',
    navBlueLight: '#C08CF0',
    isDark: true,
  },
};

// Display / cycle order for the theme pickers (Settings grid + header cycle
// button), shared shape with mobile's Appearance screen.
export const THEME_ORDER = ['blue', 'dark', 'blush', 'indigo', 'warm', 'wicked', 'sunset-light', 'sunset-dark'];

// Accent colours for the brand "i". Picked ONCE per mount / route change (never
// animated). The "i" is NEVER black and must stay legible against both the
// background AND the base "people/know" colour. Two contrast-safe pools, keyed
// off the background the wordmark actually renders on (PikBrand derives this
// from its own computed text colour, so inline usages self-correct per theme):
//   • ON_LIGHT — deep, low-luminance vivid accents for light/pale backgrounds.
//   • ON_DARK  — bright, high-luminance vivid accents for dark backgrounds.
// Both lead with blue to echo the brand reference wordmark.
const BRAND_I_ON_LIGHT = ['#1D4ED8', '#6D28D9', '#BE123C', '#047857', '#C2410C'];
const BRAND_I_ON_DARK = ['#60A5FA', '#A78BFA', '#F472B6', '#34D399', '#FBBF24'];
export function brandIColors(onDark) {
  return onDark ? BRAND_I_ON_DARK : BRAND_I_ON_LIGHT;
}

// Text-size accessibility tiers — shared vocabulary with mobile.
export const fontScales = {
  small: 0.9,
  medium: 1,
  large: 1.15,
  xlarge: 1.3,
};

// Old theme keys → new keys so existing users aren't dropped to default.
const THEME_ALIAS = { charcoal: 'dark', fuchsia: 'sunset-light' };

export function ThemeProvider({ children }) {
  const location = useLocation();
  const forceBlue = AUTH_ROUTE_RE.test(location.pathname || '');
  const [theme, setTheme] = useState('blue');
  const [fontScale, setFontScale] = useState('medium');
  const [mounted, setMounted] = useState(false);

  // Load persisted preferences on mount (client-side only).
  useEffect(() => {
    const savedTheme = localStorage.getItem('peopleiknow-theme');
    const mapped = themes[savedTheme] ? savedTheme : THEME_ALIAS[savedTheme];
    if (mapped && themes[mapped]) setTheme(mapped);

    const savedScale = localStorage.getItem('peopleiknow-font-scale');
    if (savedScale && fontScales[savedScale]) setFontScale(savedScale);

    setMounted(true);
  }, []);

  // Apply theme CSS variables + dark class.
  useEffect(() => {
    if (!mounted) return;
    // On auth routes, render Classic Blue but DON'T persist it — the user's
    // real preference is restored the moment they leave those routes.
    const activeKey = forceBlue ? 'blue' : theme;
    const themeConfig = themes[activeKey];
    if (!themeConfig) return;

    const root = document.documentElement;
    Object.entries(themeConfig.colors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    const accentText = readableAccentText(themeConfig.colors['--accent'], themeConfig.colors['--background']);
    if (accentText) root.style.setProperty('--accent-text', accentText);
    if (!forceBlue) localStorage.setItem('peopleiknow-theme', theme);

    if (themeConfig.isDark) root.classList.add('dark');
    else root.classList.remove('dark');
    root.setAttribute('data-theme', activeKey);
  }, [theme, mounted, forceBlue]);

  // Apply font scale by resizing the rem base — all Tailwind text/height
  // utilities are rem-based, so this scales glyphs, line-heights and row
  // heights together.
  useEffect(() => {
    if (!mounted) return;
    const scale = fontScales[fontScale] || 1;
    document.documentElement.style.fontSize = `${16 * scale}px`;
    localStorage.setItem('peopleiknow-font-scale', fontScale);
  }, [fontScale, mounted]);

  const value = useMemo(
    () => ({ theme, setTheme, currentTheme: themes[forceBlue ? 'blue' : theme], themes, fontScale, setFontScale, fontScales }),
    [theme, fontScale, forceBlue]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
      <div className="theme-ambience" aria-hidden="true">
        <div className="theme-ambience__sheen" />
        <div className="theme-ambience__stars">
          {AMBIENCE_SPARKS.map((s, i) => (
            <span key={i} className="pik-spark" style={{ top: s.top, left: s.left, animationDelay: s.delay }}>
              <Sparkles size={s.size} strokeWidth={2} />
            </span>
          ))}
        </div>
      </div>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

// Contact cards read the active theme even in tests that render a header
// without the provider. Missing context stays on Classic Blue.
export function useThemeKey() {
  const context = useContext(ThemeContext);
  return context?.theme || 'blue';
}
